/**
 * The one reader of the saved Perks state (`c_acLoyaltyData`): cart banner,
 * account dropdown, My Account card, analytics and the Perks refresh.
 */
import { loyaltyScopeSchema, siteLoyaltySchema } from "./loyalty-data.schemas";
import type { LoyaltyState } from "./types";

/**
 * This site's loyalty state from either blob format ({@link siteLoyaltySchema}),
 * or null when the attribute is absent, malformed, or holds no usable scope for
 * `siteId` (not enrolled here).
 */
export function parseLoyaltyData(
  /**
   * The one field it reads, not a full `Customer`: the SDK customer and
   * `app.types.Customer` aren't mutually assignable, so a `Customer` param would
   * force a cast on every caller.
   */
  customer: { c_acLoyaltyData?: string } | null | undefined,
  siteId: string,
): LoyaltyState | null {
  const site = siteLoyaltySchema(siteId).safeParse(customer?.c_acLoyaltyData);
  if (!site.success) return null;
  const scope = loyaltyScopeSchema.safeParse(site.data.scope);
  return scope.success ? scope.data : null;
}

/**
 * Annex writes "YES"/"NO"; SFRA compares case-insensitively
 * (`loyaltyHelper.isOptedInCustomer` lowercases first).
 */
export function isOptedIn(loyalty: LoyaltyState | null | undefined): boolean {
  return (loyalty?.optInStatus ?? "").toLowerCase() === "yes";
}

/**
 * The balance to show or report: 0 when not enrolled here, undefined when the
 * balance was never read, never a made-up 0.
 */
export function loyaltyPoints(
  loyalty: LoyaltyState | null,
): number | undefined {
  if (!loyalty) return 0;
  return loyalty.pointsKnown ? loyalty.availablePoints : undefined;
}
